<template>
    <div class="package-id-edit-modal">
        <Button borderOnly noMargin type="button" class="package-id-edit-trigger" @click="openModal">
            <i class="material-icons" aria-hidden="true">edit</i>
            <span>Edit package ID</span>
        </Button>

        <CustomModal
            v-if="variant"
            :title="`Edit package ID · ${variant.name}`"
            :show="showModal"
            @close="closeModal"
        >
            <form class="package-id-form" @submit.prevent="submitChange">
                <p class="package-id-intro">
                    Move current inventory to another package ID. Receiving records and completed sales stay unchanged.
                </p>

                <div v-if="loading" class="package-id-state" role="status">Loading package inventory…</div>
                <div v-else-if="loadError" class="package-id-state package-id-state-error" role="alert">
                    {{ loadError }}
                    <button type="button" @click="fetchInventory">Try again</button>
                </div>
                <template v-else>
                    <div class="package-id-grid">
                        <label :for="`${fieldPrefix}-warehouse`">
                            <span>Location</span>
                            <select
                                :id="`${fieldPrefix}-warehouse`"
                                class="form-select"
                                :value="warehouseId"
                                :disabled="saving"
                                @change="changeWarehouse($event.target.value)"
                            >
                                <option value="">Select location</option>
                                <option v-for="option of warehouseOptions" :key="option.id" :value="option.id">
                                    {{ option.title }}
                                </option>
                            </select>
                        </label>

                        <label :for="`${fieldPrefix}-room`">
                            <span>Room</span>
                            <select
                                :id="`${fieldPrefix}-room`"
                                class="form-select"
                                :value="roomKey"
                                :disabled="saving || !warehouseId"
                                @change="changeRoom($event.target.value)"
                            >
                                <option value="FRONT_ROOM">Front Room</option>
                                <option value="BACK_ROOM">Back Room</option>
                            </select>
                        </label>
                    </div>

                    <label :for="`${fieldPrefix}-source`" class="package-id-field">
                        <span>Source package ID</span>
                        <select
                            :id="`${fieldPrefix}-source`"
                            class="form-select"
                            :value="sourceLedgerId"
                            :disabled="saving || !warehouseId"
                            @change="changeSource($event.target.value)"
                        >
                            <option value="">Select package ID</option>
                            <option v-for="option of sourcePackageOptions" :key="option.id" :value="option.id">
                                {{ option.title }} · {{ formatQuantity(option.quantity) }} available
                            </option>
                        </select>
                        <small v-if="warehouseId && !sourcePackageOptions.length">No stock is available in this room.</small>
                    </label>

                    <label :for="`${fieldPrefix}-quantity`" class="package-id-field">
                        <span>Quantity</span>
                        <input
                            :id="`${fieldPrefix}-quantity`"
                            v-model="quantity"
                            class="form-control"
                            type="number"
                            min="1"
                            step="1"
                            :max="availableQuantity || undefined"
                            :disabled="saving || !sourceLedgerId"
                            inputmode="numeric"
                        />
                        <small v-if="sourceLedgerId">
                            Whole units only. Defaults to the full {{ formatQuantity(availableQuantity) }} units available in {{ roomLabel }}.
                        </small>
                    </label>

                    <fieldset class="destination-mode" :disabled="saving || !sourceLedgerId">
                        <legend>Destination package ID</legend>
                        <div class="destination-mode-options">
                            <label :class="{ active: destinationMode === 'existing' }">
                                <input v-model="destinationMode" type="radio" value="existing" />
                                <span>Existing received lot</span>
                            </label>
                            <label :class="{ active: destinationMode === 'new' }">
                                <input v-model="destinationMode" type="radio" value="new" />
                                <span>New package ID</span>
                            </label>
                        </div>
                    </fieldset>

                    <label v-if="destinationMode === 'existing'" :for="`${fieldPrefix}-existing`" class="package-id-field">
                        <span>Existing package ID</span>
                        <select
                            :id="`${fieldPrefix}-existing`"
                            v-model="existingDestinationBatchNo"
                            class="form-select"
                            :disabled="saving || !sourceLedgerId"
                        >
                            <option value="">Select an existing lot</option>
                            <option v-for="option of existingDestinationOptions" :key="option.id" :value="option.id">
                                {{ option.title }} · {{ formatQuantity(option.quantity) }} in {{ roomLabel }}
                            </option>
                        </select>
                        <small v-if="sourceLedgerId && !existingDestinationOptions.length">
                            No other received lots are available here. Choose a new package ID.
                        </small>
                    </label>

                    <label v-else :for="`${fieldPrefix}-new`" class="package-id-field">
                        <span>New package ID</span>
                        <input
                            :id="`${fieldPrefix}-new`"
                            v-model.trim="newDestinationBatchNo"
                            class="form-control"
                            type="text"
                            maxlength="80"
                            autocomplete="off"
                            :disabled="saving || !sourceLedgerId"
                            placeholder="Enter package or LOT ID"
                        />
                        <small>Use letters, numbers, periods, underscores, slashes, or hyphens.</small>
                    </label>

                    <div v-if="errorMessage" class="package-id-error" role="alert">{{ errorMessage }}</div>

                    <div class="package-id-summary" aria-live="polite">
                        <span>Inventory move</span>
                        <strong>{{ changeSummary }}</strong>
                    </div>

                    <div class="package-id-actions">
                        <Button noMargin borderOnly type="button" :disabled="saving" @click="closeModal">Cancel</Button>
                        <Button noMargin type="submit" :disabled="saving || !canSubmit">
                            {{ saving ? "Saving…" : "Move to package ID" }}
                        </Button>
                    </div>
                </template>
            </form>
        </CustomModal>
    </div>
</template>

<script>
import { mapState } from "vuex"
import Button from "../UI/Button.vue"
import CustomModal from "../layout/CustomModal.vue"
import { http } from "../../utils/http"
import { errMsg } from "../../utils/common"

const PACKAGE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,79}$/
const FRONT_ROOM_KEY = "FRONT_ROOM"
const BACK_ROOM_KEY = "BACK_ROOM"

export default {
    components: { Button, CustomModal },
    props: {
        variant: {
            type: Object,
            required: true,
        },
        selectedWarehouseId: {
            type: String,
            default: "",
        },
        selectedRoomKey: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            showModal: false,
            loading: false,
            saving: false,
            inventory: null,
            warehouseId: "",
            roomKey: FRONT_ROOM_KEY,
            sourceLedgerId: "",
            quantity: "",
            destinationMode: "existing",
            existingDestinationBatchNo: "",
            newDestinationBatchNo: "",
            errorMessage: "",
            loadError: "",
        }
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        fieldPrefix() {
            return `package-id-${String(this.variant.id || "item").replace(/[^A-Za-z0-9_-]/g, "-")}`
        },
        ledgerEntries() {
            return (this.inventory && this.inventory.ledgerEntries) || []
        },
        warehouseOptions() {
            const warehouseIds = new Set(this.ledgerEntries.map((entry) => String(entry.warehouse || "")).filter(Boolean))
            const knownWarehouses = (this.warehouses || [])
                .filter((warehouse) => warehouseIds.has(String(warehouse.id)))
                .map((warehouse) => ({ id: String(warehouse.id), title: warehouse.name }))
            const knownIds = new Set(knownWarehouses.map((warehouse) => warehouse.id))
            const inventoryWarehouses = ((this.inventory && this.inventory.warehouseEntries) || [])
                .map((entry) => entry.warehouse)
                .filter((warehouse) => warehouse && warehouse.id && !knownIds.has(String(warehouse.id)))
                .map((warehouse) => ({ id: String(warehouse.id), title: warehouse.name }))
            const resolvedIds = new Set([...knownWarehouses, ...inventoryWarehouses].map((warehouse) => warehouse.id))
            const fallbackWarehouses = Array.from(warehouseIds)
                .filter((warehouseId) => !resolvedIds.has(warehouseId))
                .map((warehouseId) => ({ id: warehouseId, title: warehouseId }))
            return [...knownWarehouses, ...inventoryWarehouses, ...fallbackWarehouses]
        },
        sourcePackageOptions() {
            return this.ledgerEntries
                .filter((entry) => {
                    if (String(entry.warehouse || "") !== this.warehouseId || Number(entry.is_cancelled || 0) === 1) return false
                    if (this.roomKey === FRONT_ROOM_KEY && this.isCustomRoom(entry.room_name)) return false
                    return this.roomQuantity(entry) > 0
                })
                .map((entry) => ({
                    id: String(entry.id),
                    title: String(entry.batch_no || "Unassigned"),
                    quantity: this.roomQuantity(entry),
                    entry,
                }))
                .sort((left, right) => left.title.localeCompare(right.title))
        },
        selectedSourceEntry() {
            const option = this.sourcePackageOptions.find((item) => item.id === this.sourceLedgerId)
            return option ? option.entry : null
        },
        availableQuantity() {
            return this.selectedSourceEntry ? this.roomQuantity(this.selectedSourceEntry) : 0
        },
        existingDestinationOptions() {
            const sourceBatchNo = String((this.selectedSourceEntry && this.selectedSourceEntry.batch_no) || "")
            const byBatch = new Map()
            this.ledgerEntries.forEach((entry) => {
                const batchNo = String(entry.batch_no || "")
                if (
                    !batchNo ||
                    batchNo === "1000" ||
                    batchNo === sourceBatchNo ||
                    String(entry.warehouse || "") !== this.warehouseId ||
                    Number(entry.is_cancelled || 0) === 1 ||
                    this.isCustomRoom(entry.room_name)
                ) {
                    return
                }
                const current = byBatch.get(batchNo) || { id: batchNo, title: batchNo, quantity: 0 }
                current.quantity += this.roomQuantity(entry)
                byBatch.set(batchNo, current)
            })
            return Array.from(byBatch.values()).sort((left, right) => left.title.localeCompare(right.title))
        },
        destinationBatchNo() {
            return this.destinationMode === "existing"
                ? this.existingDestinationBatchNo
                : this.newDestinationBatchNo.trim()
        },
        roomLabel() {
            return this.roomKey === BACK_ROOM_KEY ? "Back Room" : "Front Room"
        },
        canSubmit() {
            const quantity = Number(this.quantity)
            if (!this.warehouseId || !this.selectedSourceEntry || !Number.isSafeInteger(quantity) || quantity <= 0) return false
            if (quantity > this.availableQuantity) return false
            if (!this.destinationBatchNo) return false
            if (this.destinationMode === "new") {
                return PACKAGE_ID_PATTERN.test(this.destinationBatchNo) && this.destinationBatchNo !== "1000"
            }
            return this.existingDestinationOptions.some((option) => option.id === this.destinationBatchNo)
        },
        changeSummary() {
            if (!this.selectedSourceEntry) return "Select a source package to preview the change."
            if (!this.destinationBatchNo) return `${this.selectedSourceEntry.batch_no} → select a destination`
            return `${this.formatQuantity(Number(this.quantity) || 0)} units · ${this.selectedSourceEntry.batch_no} → ${this.destinationBatchNo}`
        },
    },
    methods: {
        openModal() {
            this.showModal = true
            this.fetchInventory()
        },
        closeModal() {
            if (this.saving) return
            this.showModal = false
            this.errorMessage = ""
        },
        isCustomRoom(roomName) {
            const normalized = String(roomName || "").trim().toLowerCase()
            return !!normalized && normalized !== "front room" && normalized !== "back room"
        },
        roomQuantity(entry) {
            return this.roomKey === BACK_ROOM_KEY
                ? Number(entry.stock_value_back_room || 0)
                : Number(entry.stock_value || 0)
        },
        formatQuantity(value) {
            return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })
        },
        resetDestination() {
            this.destinationMode = "existing"
            this.existingDestinationBatchNo = ""
            this.newDestinationBatchNo = ""
            this.errorMessage = ""
        },
        chooseSourceDefault() {
            this.sourceLedgerId = this.sourcePackageOptions.length === 1 ? this.sourcePackageOptions[0].id : ""
            this.quantity = this.sourceLedgerId ? String(this.availableQuantity) : ""
            this.resetDestination()
        },
        changeWarehouse(warehouseId) {
            this.warehouseId = warehouseId
            const preferredRoom = [FRONT_ROOM_KEY, BACK_ROOM_KEY].includes(this.selectedRoomKey)
                ? this.selectedRoomKey
                : FRONT_ROOM_KEY
            this.roomKey = preferredRoom
            if (!this.sourcePackageOptions.length) {
                this.roomKey = preferredRoom === FRONT_ROOM_KEY ? BACK_ROOM_KEY : FRONT_ROOM_KEY
            }
            this.chooseSourceDefault()
        },
        changeRoom(roomKey) {
            this.roomKey = roomKey
            this.chooseSourceDefault()
        },
        changeSource(sourceLedgerId) {
            this.sourceLedgerId = sourceLedgerId
            this.quantity = sourceLedgerId ? String(this.availableQuantity) : ""
            this.resetDestination()
        },
        async fetchInventory() {
            this.loading = true
            this.loadError = ""
            this.errorMessage = ""
            try {
                const response = await http.get(
                    `/stock/inventory?variants=true&item_id=${encodeURIComponent(this.variant.id)}&return_ledger_entries=true&out_of_stock=1`
                )
                this.inventory = response.data.result
                const availableWarehouseIds = this.warehouseOptions.map((option) => option.id)
                const nextWarehouseId = availableWarehouseIds.includes(String(this.selectedWarehouseId || ""))
                    ? String(this.selectedWarehouseId)
                    : availableWarehouseIds[0] || ""
                this.changeWarehouse(nextWarehouseId)
            } catch (error) {
                this.inventory = null
                this.warehouseId = ""
                this.sourceLedgerId = ""
                this.quantity = ""
                this.loadError = errMsg(error, "Package inventory could not be loaded.")
            } finally {
                this.loading = false
            }
        },
        validationMessage() {
            const quantity = Number(this.quantity)
            if (!this.warehouseId) return "Select a location."
            if (!this.selectedSourceEntry) return "Select a source package ID."
            if (!Number.isSafeInteger(quantity) || quantity <= 0) return "Quantity must be a whole number greater than zero."
            if (quantity > this.availableQuantity) return `Quantity cannot exceed ${this.formatQuantity(this.availableQuantity)} units.`
            if (!this.destinationBatchNo) return "Select or enter a destination package ID."
            if (String(this.selectedSourceEntry.batch_no) === this.destinationBatchNo) return "Destination package ID must differ from the source."
            if (this.destinationMode === "new" && !PACKAGE_ID_PATTERN.test(this.destinationBatchNo)) {
                return "Use 1-80 letters, numbers, periods, underscores, slashes, or hyphens."
            }
            if (this.destinationMode === "new" && this.destinationBatchNo === "1000") {
                return "1000 is reserved for inventory without a package ID."
            }
            return ""
        },
        async submitChange() {
            if (this.saving) return
            this.errorMessage = this.validationMessage()
            if (this.errorMessage) return

            this.saving = true
            try {
                const response = await http.post("/stock/package-id", {
                    source_ledger_id: this.selectedSourceEntry.id,
                    warehouse_id: this.warehouseId,
                    room: this.roomKey,
                    quantity: Number(this.quantity),
                    expected_source_quantity: this.availableQuantity,
                    expected_source_updated_at: this.selectedSourceEntry.updatedAt || null,
                    destination_mode: this.destinationMode,
                    destination_batch_no: this.destinationBatchNo,
                })
                this.$emit("update", response.data.result)
                this.$toast.success(response.data.message || "Package ID updated.")
                this.showModal = false
            } catch (error) {
                this.errorMessage = errMsg(error, "Package ID could not be changed.")
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.package-id-edit-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: $component-height;
    white-space: nowrap;

    i {
        font-size: 17px;
    }
}

.package-id-edit-modal :deep(.custom-dialog) {
    box-sizing: border-box;
    top: 50%;
    width: min(560px, calc(100vw - 32px));
    max-width: 560px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    transform: translate(-50%, -50%);
    white-space: normal;
}

.package-id-edit-modal :deep(.custom-title) {
    min-width: 0;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.package-id-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    color: $textColor;
}

.package-id-intro {
    margin: 0;
    color: rgba($textColor, 0.7);
    font-size: 13px;
    line-height: 1.45;
    overflow-wrap: anywhere;
}

.package-id-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.package-id-grid label,
.package-id-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
    min-width: 0;

    .form-control,
    .form-select {
        width: 100%;
        min-width: 0;
    }

    > span {
        font-size: 13px;
        font-weight: 700;
    }

    small {
        color: rgba($textColor, 0.64);
        font-size: 12px;
        line-height: 1.35;
    }
}

.destination-mode {
    margin: 0;
    padding: 0;
    border: 0;

    legend {
        margin-bottom: 7px;
        color: $textColor;
        font-size: 13px;
        font-weight: 700;
    }
}

.destination-mode-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;

    label {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        padding: 9px 10px;
        border: 1px solid $lightBorderColor;
        border-radius: $cardRadius;
        cursor: pointer;
        font-size: 13px;

        &.active {
            border-color: $primaryColor;
            background: rgba($primaryColor, 0.06);
        }
    }
}

.package-id-summary {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 12px;
    border: 1px solid $lightBorderColor;
    border-radius: $cardRadius;
    background: $lightHoverColor;

    span {
        color: rgba($textColor, 0.64);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    strong {
        font-size: 13px;
        overflow-wrap: anywhere;
    }
}

.package-id-state {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba($textColor, 0.7);
    font-size: 14px;
}

.package-id-state-error {
    flex-direction: column;
    gap: 8px;
    color: $dangerColor;

    button {
        border: 0;
        background: transparent;
        color: $primaryColor;
        text-decoration: underline;
        cursor: pointer;
    }
}

.package-id-error {
    padding: 9px 10px;
    border: 1px solid rgba($dangerColor, 0.35);
    border-radius: $cardRadius;
    background: rgba($dangerColor, 0.07);
    color: darken($dangerColor, 8%);
    font-size: 13px;
}

.package-id-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

:deep(.dark) .package-id-form {
    color: $darkTextColor;

    .package-id-intro,
    .package-id-field small {
        color: rgba($darkTextColor, 0.68);
    }

    .form-control,
    .form-select {
        border-color: $darkBorderColor;
        background: $darkInputBg;
        color: $darkTextColor;
    }

    .form-control::placeholder {
        color: rgba($darkTextColor, 0.58);
    }

    .destination-mode legend {
        color: $darkTextColor;
    }

    .destination-mode-options label {
        border-color: $darkBorderColor;
        background: transparent;

        &.active {
            border-color: $secondaryColorDark;
            background: rgba($secondaryColorDark, 0.1);
        }
    }

    .package-id-summary {
        border-color: $darkBorderColor;
        background: $darkInputBg;

        span {
            color: rgba($darkTextColor, 0.64);
        }
    }

    .package-id-state {
        color: rgba($darkTextColor, 0.7);
    }

    .package-id-error {
        color: $dangerColorDark;
        border-color: rgba($dangerColorDark, 0.45);
        background: rgba($dangerColorDark, 0.1);
    }
}

@media (max-width: 600px) {
    .package-id-edit-modal :deep(.custom-dialog) {
        width: calc(100vw - 24px);
        max-height: calc(100vh - 24px);
        padding: 12px;
    }

    .package-id-grid,
    .destination-mode-options {
        grid-template-columns: 1fr;
    }

    .package-id-actions {
        flex-direction: column-reverse;

        :deep(.app-btn) {
            width: 100%;
        }
    }
}
</style>
